import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DialogFooter,
  Form,
  FormField,
  FormInput,
  Button,
  useMultiStepForm,
  FormToggleGroup,
  ToggleGroup,
  ToggleGroupItem,
  V2Label,
  cn,
} from "web-design-system";
import { BANK_ACCOUNT_TYPE, NRI_BANK_ACCOUNT_TYPE } from "creso-core/constants";
import {
  useUpdateInvestorBankDetails,
  useCreateInvestorBankDetails,
  useInvestorPrimaryBankAccount,
  useVerifyBankDetails,
  useInvestorProfile,
  useBankServiceCapabilitiesByIfsc,
} from "creso-core/hooks";
import { BankDetailsSchema } from "creso-core/zod";
import {
  getBankServiceCapabilityWarningMessage,
  sleep,
} from "creso-core/utils";
import {
  CheckCheckIcon,
  InfoIcon,
  Loader2,
  TriangleAlertIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Client, InvestorBankDetail } from "creso-core/types";
import { APIError, handleError } from "../../utils";
import { useClientOnboarding } from "./context";
import { AutofilledData } from "./autofilled-data";
import { useMinorOnboarding } from "./minor-form-data";

const TOGGLE_ITEM_CLASS =
  "px-3 py-2 border-[1.5px] text-primary font-normal rounded-lg data-[state=on]:border-primary data-[state=on]:font-semibold";

const BANK_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  BANK_ACCOUNT_TYPE.map((type) => [type.value, type.label])
);

const maskAccountNumber = (accountNumber: string) =>
  `${"X".repeat(Math.max(accountNumber.length - 4, 0))}${accountNumber.slice(-4)}`;

/** The guardian's existing account, shown read-only for a minor */
function GuardianBankAccount({
  account,
}: {
  account: InvestorBankDetail | undefined;
}) {
  if (!account) {
    return (
      <span className="text-sm text-muted-foreground">
        No bank account found for the guardian.
      </span>
    );
  }

  const rows = [
    ["Bank", account.bank_name],
    ["Account number", maskAccountNumber(account.account_number)],
    ["IFSC code", account.ifsc_code],
    ["Account type", BANK_TYPE_LABELS[account.type] ?? account.type],
  ];

  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-secondary text-sm">
      {rows.map(([label, value], index) => (
        <div
          key={label}
          className={cn(
            "flex justify-between gap-4 px-4 py-3",
            index > 0 && "border-t border-border"
          )}
        >
          <span className="text-muted-foreground">{label}</span>
          <span className="font-medium">{value}</span>
        </div>
      ))}
    </div>
  );
}

const MAX_ATTEMPTS = 3;

/**
 * `nri` swaps Savings / Current for NRE / NRO.
 * `minor` lets the account be the minor's (verified as usual) or the guardian's.
 */
export function BankDetails({
  nri = false,
  minor = false,
}: {
  nri?: boolean;
  minor?: boolean;
}) {
  const { client, refetchClient } = useClientOnboarding();
  const { guardian } = useMinorOnboarding();
  const { previousStep, nextStep } = useMultiStepForm();
  const investmentAccountGuid = client?.investment_account ?? "";
  const { data: bankDetails, refetch } = useInvestorPrimaryBankAccount(
    investmentAccountGuid
  );
  const [accountHolder, setAccountHolder] = useState<"minor" | "guardian">(
    bankDetails?.account_holder ?? "minor"
  );
  const guardianHoldsAccount = minor && accountHolder === "guardian";
  // A guardian who is your client already has a verified account, which is
  // copied onto the minor; a new guardian's account is entered and verified
  const usesGuardianAccount = guardianHoldsAccount && !!guardian?.client;
  const { data: guardianAccount } = useInvestorPrimaryBankAccount(
    usesGuardianAccount ? (guardian?.client?.investment_account ?? "") : ""
  );
  const hasCopiedGuardianAccount =
    bankDetails?.account_holder === "guardian" &&
    bankDetails.verification_status === "verified";
  const [isCopying, setIsCopying] = useState(false);
  const [nriAccountType, setNriAccountType] = useState(
    NRI_BANK_ACCOUNT_TYPE.some((t) => t.value === bankDetails?.type)
      ? (bankDetails?.type as string)
      : NRI_BANK_ACCOUNT_TYPE[0].value
  );
  // The saved account loads after the first render
  useEffect(() => {
    if (bankDetails?.account_holder)
      setAccountHolder(bankDetails.account_holder);
    if (NRI_BANK_ACCOUNT_TYPE.some((t) => t.value === bankDetails?.type)) {
      setNriAccountType(bankDetails?.type as string);
    }
  }, [bankDetails?.account_holder, bankDetails?.type]);
  // e.g. the PAN already has an NRO account
  const [nriAccountTypeError, setNriAccountTypeError] = useState<string | null>(
    null
  );

  const { data: profile } = useInvestorProfile(
    { investment_account_guid: investmentAccountGuid ?? "" },
    {
      enabled: true,
    }
  );

  const { mutateAsync: createBankDetails } = useCreateInvestorBankDetails();

  const { mutateAsync: updateBankDetails } = useUpdateInvestorBankDetails();

  const { mutateAsync: verifyBankDetails } = useVerifyBankDetails();

  const form = useForm<z.infer<typeof BankDetailsSchema>>({
    resolver: zodResolver(BankDetailsSchema),
    values: {
      accountNumber: bankDetails?.account_number ?? "",
      ifsc: bankDetails?.ifsc_code ?? "",
      // NRE / NRO live outside the form, which only knows Savings / Current
      type: (!nri && bankDetails?.type) || BANK_ACCOUNT_TYPE[0].value,
      confirmAccountNumber: bankDetails?.account_number ?? "",
      primaryAccountHolderName:
        profile?.name ||
        bankDetails?.primary_account_holder_name ||
        client?.name ||
        "",
    },
  });

  const ifscCode = form.watch("ifsc");
  const { data: bankServiceCapabilities } = useBankServiceCapabilitiesByIfsc(
    ifscCode,
    {
      enabled: ifscCode.length >= 11,
    }
  );

  const [isVerifying, setIsVerifying] = useState(false);

  const onSubmit = async (data: z.infer<typeof BankDetailsSchema>) => {
    const payload = {
      account_number: data.accountNumber,
      ifsc_code: data.ifsc,
      type: nri ? nriAccountType : data.type,
      investment_account_guid: investmentAccountGuid,
      // The ₹1 check only accepts the account holder's name
      primary_account_holder_name: guardianHoldsAccount
        ? guardian?.name
        : data.primaryAccountHolderName,
      autofilled: bankDetails?.autofilled ? !isDirty : false,
      ...(minor ? { account_holder: accountHolder } : {}),
    };

    try {
      setIsVerifying(true);
      setNriAccountTypeError(null);
      if (bankDetails?.id) {
        await updateBankDetails(payload);
      } else {
        await createBankDetails(payload);
      }
      // NRE / NRO sets the client's tax status
      if (nri) await refetchClient();
      try {
        await verifyBankDetails(investmentAccountGuid);
        let details = await refetch();
        let attempts = 1;
        while (
          details.data?.verification_status === "unverified" &&
          attempts <= MAX_ATTEMPTS
        ) {
          // eslint-disable-next-line no-await-in-loop
          await sleep(5000);
          // eslint-disable-next-line no-await-in-loop
          details = await refetch();

          attempts += 1;
        }
      } catch {
        // Ingore error since we display this inline
      }
    } catch (error) {
      const typeError =
        error instanceof APIError &&
        (error.data?.details as { type?: string | string[] } | undefined)?.type;
      if (nri && typeError) {
        setNriAccountTypeError(
          Array.isArray(typeError) ? typeError[0] : typeError
        );
      } else {
        handleError(error, "Failed to update bank details");
      }
    } finally {
      setIsVerifying(false);
    }
    return true;
  };

  const handleUseGuardianAccount = async () => {
    if (!guardianAccount) return;
    try {
      setIsCopying(true);
      await createBankDetails({
        investment_account_guid: investmentAccountGuid,
        copy_from_bank_account_guid: guardianAccount.bank_account_guid,
      });
      await refetch();
    } catch (error) {
      handleError(error, "Failed to use the guardian's account");
    } finally {
      setIsCopying(false);
    }
  };

  const { isDirty, isSubmitted } = form.formState;

  const bankAccountStatus = bankDetails?.verification_status;

  const showVerifyButton =
    isDirty ||
    !bankDetails?.bank_account_guid ||
    isVerifying ||
    (!isSubmitted && bankAccountStatus !== "verified");

  const showSkipButton =
    !showVerifyButton && isSubmitted && bankAccountStatus !== "verified";

  const showNextButton =
    !showVerifyButton && !showSkipButton && bankAccountStatus === "verified";

  const bankCapabilityWarningMessage = getBankServiceCapabilityWarningMessage(
    bankServiceCapabilities
  );

  return (
    <div className="flex flex-col size-full p-6 gap-6">
      <span className="text-lg font-semibold">Bank details</span>
      <Form {...form}>
        <form
          className="flex flex-col flex-1 gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          {minor && (
            <div className="flex flex-col gap-1.5">
              <V2Label>Account holder</V2Label>
              <ToggleGroup
                type="single"
                disabled={isVerifying}
                value={accountHolder}
                onValueChange={(value) =>
                  value && setAccountHolder(value as "minor" | "guardian")
                }
                className="w-fit gap-2 justify-start bg-background"
              >
                <ToggleGroupItem
                  value="minor"
                  variant="outline"
                  className={TOGGLE_ITEM_CLASS}
                >
                  Minor
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="guardian"
                  variant="outline"
                  className={TOGGLE_ITEM_CLASS}
                >
                  Guardian
                </ToggleGroupItem>
              </ToggleGroup>
              {accountHolder === "minor" && (
                <span className="text-xs text-muted-foreground">
                  Includes minor under guardian
                </span>
              )}
            </div>
          )}
          {usesGuardianAccount && (
            <>
              <GuardianBankAccount account={guardianAccount} />
              <span className="text-xs text-destructive">
                Redemptions need an account in the minor&apos;s name
              </span>
            </>
          )}
          {nri && (
            <div className="flex flex-col gap-1.5">
              <V2Label>Account type</V2Label>
              <ToggleGroup
                type="single"
                disabled={isVerifying}
                value={nriAccountType}
                onValueChange={(value) => {
                  if (!value) return;
                  setNriAccountType(value);
                  setNriAccountTypeError(null);
                }}
                className="w-fit gap-2 justify-start bg-background"
              >
                {NRI_BANK_ACCOUNT_TYPE.map((option) => (
                  <ToggleGroupItem
                    key={option.value}
                    value={option.value}
                    variant="outline"
                    className="px-3 py-2 border-[1.5px] text-primary font-normal rounded-lg data-[state=on]:border-primary data-[state=on]:font-semibold"
                  >
                    {option.label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
              {!!nriAccountTypeError && (
                <span className="text-xs text-destructive">
                  {nriAccountTypeError}
                </span>
              )}
            </div>
          )}
          {!usesGuardianAccount && (
            <>
              <FormField
                control={form.control}
                name="accountNumber"
                render={({ field }) => (
                  <FormInput
                    disabled={isVerifying}
                    label="Account number"
                    placeholder="1234567890"
                    {...field}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="confirmAccountNumber"
                render={({ field }) => (
                  <FormInput
                    disabled={
                      !form.getFieldState("accountNumber").isDirty ||
                      isVerifying
                    }
                    label="Confirm account number"
                    {...field}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="ifsc"
                render={({ field }) => (
                  <FormInput
                    disabled={isVerifying}
                    placeholder="SBIN0000489"
                    label="IFSC code"
                    {...field}
                    onChange={(value) => {
                      field.onChange(value?.toLocaleUpperCase() ?? "");
                    }}
                  />
                )}
              />
              {!nri && (
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormToggleGroup
                      disabled={isVerifying}
                      label="Account type"
                      options={BANK_ACCOUNT_TYPE.map((type) => ({
                        label: type.label,
                        value: type.value,
                      }))}
                      {...field}
                    />
                  )}
                />
              )}
            </>
          )}
          <div
            className={cn(
              "mt-auto flex w-full text-foreground",
              usesGuardianAccount && "hidden"
            )}
          >
            {!isVerifying &&
              !isDirty &&
              bankDetails?.verification_status === "failed" && (
                <div className="flex gap-2 p-3 w-full text-sm leading-5 rounded-lg bg-tag-orange text-tag-orange-foreground items-start">
                  <TriangleAlertIcon className="size-4 flex-shrink-0 mt-0.5" />
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold">Verification Failed</span>
                    <span className="text-sm">
                      {bankDetails?.verification_remark}
                      {minor &&
                        " If this is the guardian's account, choose Guardian."}
                    </span>
                  </div>
                </div>
              )}
            {!isVerifying &&
              !isDirty &&
              bankDetails?.verification_status === "verified" && (
                <div className="flex flex-col gap-1.5 px-1.5 py-3 w-full text-sm items-center leading-5 rounded-lg bg-tag-green text-tag-green-foreground">
                  <div className="flex gap-2 items-center justify-start w-full px-1.5">
                    <CheckCheckIcon className="size-4 text-tag-green-foreground flex-shrink-0" />
                    <span className="font-semibold">
                      {minor
                        ? `Verified for ${
                            guardianHoldsAccount
                              ? (guardian?.name ?? "")
                              : (client?.name ?? "")
                          }`
                        : "Verified"}
                    </span>
                  </div>
                  {bankCapabilityWarningMessage ? (
                    <div className="flex flex-row gap-2 items-center w-full px-2">
                      <InfoIcon className="size-3.5 flex-shrink-0 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        {bankCapabilityWarningMessage}
                      </span>
                    </div>
                  ) : null}
                </div>
              )}
            {isVerifying && (
              <div className="flex flex-col w-full text-sm leading-5 gap-2 bg-tag-slate text-tag-slate-foreground rounded-lg p-3">
                <div className="flex gap-2 items-center">
                  <Loader2 className="size-4 animate-spin flex-shrink-0" />
                  <span className="font-semibold tracking-tight">
                    Account will be verified by sending ₹1
                  </span>
                </div>
                <span>
                  Verifying account details. This may take upto 15 seconds.
                </span>
              </div>
            )}
            {!isVerifying &&
              isSubmitted &&
              bankAccountStatus === "unverified" &&
              !form.formState.isDirty && (
                <div className="flex flex-row items-center text-sm gap-2 bg-tag-slate text-tag-slate-foreground rounded-lg p-3 leading-5">
                  <InfoIcon className="size-4 flex-shrink-0" />
                  <span>
                    Verification is taking longer than expected. We&apos;ll do
                    this for you in the background.
                  </span>
                </div>
              )}
          </div>
          <DialogFooter
            className={cn(
              "flex sm:flex-col gap-4",
              // The block above is hidden then, so the footer has to push itself down
              usesGuardianAccount && "mt-auto"
            )}
          >
            <AutofilledData
              client={client as Client}
              autofillSource={!isDirty ? bankDetails?.autofill_source : null}
            />
            <div className="flex items-center w-full gap-5">
              <Button variant="outline" type="button" onClick={previousStep}>
                Previous
              </Button>
              {usesGuardianAccount &&
                (hasCopiedGuardianAccount ? (
                  <Button
                    className="w-full flex-1"
                    type="button"
                    onClick={nextStep}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    className="w-full flex-1"
                    type="button"
                    disabled={!guardianAccount || isCopying}
                    onClick={handleUseGuardianAccount}
                  >
                    {isCopying && <Loader2 className="size-4 animate-spin" />}
                    Use this account
                  </Button>
                ))}
              {/** If verification is not triggered, show the Verify button */}
              {!usesGuardianAccount && showVerifyButton && (
                <Button
                  className="w-full flex-1"
                  type="submit"
                  disabled={isVerifying}
                >
                  Verify
                </Button>
              )}
              {/** If verification hasn't completed in 15s, show the skip button */}
              {!usesGuardianAccount && showSkipButton && (
                <Button
                  className="w-full flex-1"
                  type="button"
                  onClick={nextStep}
                >
                  Skip
                </Button>
              )}
              {/** Show the next button when
               * status is verified and form is not dirty
               * form is submitted for verification but the status is still unverified (timeout)
               */}
              {!usesGuardianAccount && showNextButton && (
                <Button
                  className="w-full flex-1"
                  type="button"
                  onClick={nextStep}
                >
                  Next
                </Button>
              )}
            </div>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}
